/* eslint-disable zocdoc/no-class-name, react/no-children-prop */
import cx from 'classnames';
import { memoize } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import SimpleMarkdown from 'simple-markdown';
import { injectGlobal } from 'styled-components';

import { FontFamily } from '../typography';

export const MD_CLASSNAME_PREFIX = 'component-sdk-md';
export const CLASSES = {
    ANCHOR: `${MD_CLASSNAME_PREFIX}-anchor`,
    BOLD: `${MD_CLASSNAME_PREFIX}-bold`,
    HEADING_1: `${MD_CLASSNAME_PREFIX}-heading-1`,
    HEADING_2: `${MD_CLASSNAME_PREFIX}-heading-2`,
    HEADING_3: `${MD_CLASSNAME_PREFIX}-heading-3`,
    HEADING_4: `${MD_CLASSNAME_PREFIX}-heading-4`,
    HEADING_5: `${MD_CLASSNAME_PREFIX}-heading-5`,
    HEADING_6: `${MD_CLASSNAME_PREFIX}-heading-6`,
    IMAGE: `${MD_CLASSNAME_PREFIX}-image`,
    INLINE_CODE: `${MD_CLASSNAME_PREFIX}-inline-code`,
    ITALIC: `${MD_CLASSNAME_PREFIX}-italic`,
    MONOSPACE: `${MD_CLASSNAME_PREFIX}-monospace`,
    ORDERED_LIST: `${MD_CLASSNAME_PREFIX}-ordered-list`,
    PARAGRAPH: `${MD_CLASSNAME_PREFIX}-paragraph`,
    UNORDERED_LIST: `${MD_CLASSNAME_PREFIX}-unordered-list`,
};

export const getHeadingClassForLevel = level => {
    /* eslint-disable no-multi-spaces */
    switch (level) {
        case 1:  return CLASSES.HEADING_1;
        case 2:  return CLASSES.HEADING_2;
        case 3:  return CLASSES.HEADING_3;
        case 4:  return CLASSES.HEADING_4;
        case 5:  return CLASSES.HEADING_5;
        default: return CLASSES.HEADING_6;
    }
};

/**
 * We use `injectGlobal` here, rather than inline component styles in case the consumer decides to provide a custom
 * wrapper component. Since styled-components CSS is strongly tied to a component, we get around that by using a
 * predefined (and hopefully unique) CSS classes to tag our styles onto.
 */
injectGlobal`
    .${CLASSES.ANCHOR} {
        border-bottom: 1px dotted;
        display: inline-block;
        line-height: 1.3;
    }

    .${CLASSES.IMAGE} {
        max-height: 300px;
    }

    .${CLASSES.INLINE_CODE} {
        background: rgba(0, 0, 0, 0.05);
        font-family: Monaco, Consolas, monospace;
    }

    .${CLASSES.HEADING_1},
    .${CLASSES.HEADING_2},
    .${CLASSES.HEADING_3},
    .${CLASSES.HEADING_4},
    .${CLASSES.HEADING_5},
    .${CLASSES.HEADING_6} {
        font-family: ${FontFamily.SharpSansBold};
        margin-bottom: 1em;
    }

    .${CLASSES.HEADING_1} {
        font-size: 2em;
    }

    .${CLASSES.HEADING_2} {
        font-size: 1.8em;
    }

    .${CLASSES.HEADING_3} {
        font-size: 1.6em;
    }

    .${CLASSES.HEADING_4} {
        font-size: 1.4em;
    }

    .${CLASSES.HEADING_5} {
        font-size: 1.2em;
    }

    .${CLASSES.HEADING_6} {
        font-size: 1em;
    }

    .${CLASSES.BOLD} {
        font-family: ${FontFamily.SharpSansBold};
    }

    .${CLASSES.ITALIC} {
        font-style: italic;
    }

    .${CLASSES.ORDERED_LIST} {
        list-style: inside decimal;
    }

    .${CLASSES.UNORDERED_LIST} {
        list-style: inside disc;
    }

    .${CLASSES.MONOSPACE},
    .${CLASSES.ORDERED_LIST},
    .${CLASSES.PARAGRAPH},
    .${CLASSES.UNORDERED_LIST} {
        margin-bottom: 1.3em;
    }
`;

const HEADING_CODE_REGEX = /^(#{1,6})\s*([^\n]*)/;
const INLINE_CODE_REGEX = /^(`)([^`]*?)`/;

export const DEFAULT_RULES = {
    ...SimpleMarkdown.defaultRules,

    codeBlock: {
        ...SimpleMarkdown.defaultRules.codeBlock,

        react: (node, __, state) => {
            return (
                <pre
                    key={state.key}
                    className={CLASSES.MONOSPACE}
                >
                    <code className={cx({ [`lang-${node.lang}`]: !!node.lang })}>
                        {node.content}
                    </code>
                </pre>
            );
        },
    },

    em: {
        ...SimpleMarkdown.defaultRules.em,

        react: (node, output, state) => {
            return (
                <em
                    key={state.key}
                    className={CLASSES.ITALIC}
                >
                    {output(node.content, state)}
                </em>
            );
        },
    },

    heading: {
        ...SimpleMarkdown.defaultRules.heading,

        // modified version of https://github.com/Khan/simple-markdown/issues/30
        match: SimpleMarkdown.blockRegex(HEADING_CODE_REGEX),

        react: (node, output, state) => React.createElement(`h${node.level}`, {
            children: output(node.content, state),
            className: getHeadingClassForLevel(node.level),
            key: state.key,
        }),
    },

    image: {
        ...SimpleMarkdown.defaultRules.image,

        react: (node, __, state) => {
            return (
                <img
                    key={state.key}
                    alt={node.alt}
                    className={CLASSES.IMAGE}
                    src={SimpleMarkdown.sanitizeUrl(node.target)}
                    title={node.title}
                />
            );
        },
    },

    inlineCode: {
        ...SimpleMarkdown.defaultRules.inlineCode,

        // supports inline code that contains a newline
        match: SimpleMarkdown.inlineRegex(INLINE_CODE_REGEX),

        react: (node, __, state) => {
            return (
                <code
                    key={state.key}
                    className={CLASSES.INLINE_CODE}
                >
                    {node.content}
                </code>
            );
        },
    },

    link: {
        ...SimpleMarkdown.defaultRules.link,

        react: (node, output, state) => {
            return (
                <a
                    key={state.key}
                    className={CLASSES.ANCHOR}
                    href={SimpleMarkdown.sanitizeUrl(node.target)}
                    rel="noopener noreferrer"
                    target="_blank"
                    title={node.title}
                >
                    {output(node.content, state)}
                </a>
            );
        },
    },

    list: {
        ...SimpleMarkdown.defaultRules.list,

        react: (node, output, state) => React.createElement(node.ordered ? 'ol' : 'ul', {
            children: node.items.map((item, index) => (
                <li key={index}>
                    {output(item, state)}
                </li>
            )),
            className: node.ordered ? CLASSES.ORDERED_LIST : CLASSES.UNORDERED_LIST,
            key: state.key,
            start: node.start,
        }),
    },

    paragraph: {
        ...SimpleMarkdown.defaultRules.paragraph,

        react: (node, output, state) => {
            return (
                <p
                    key={state.key}
                    className={CLASSES.PARAGRAPH}
                >
                    {output(node.content, state)}
                </p>
            );
        },
    },

    strong: {
        ...SimpleMarkdown.defaultRules.strong,

        react: (node, output, state) => {
            return (
                <strong
                    key={state.key}
                    className={CLASSES.BOLD}
                >
                    {output(node.content, state)}
                </strong>
            );
        },
    },
};

const getParserForRuleset = memoize((rules = DEFAULT_RULES) => SimpleMarkdown.parserFor(rules));
const getEmitterForRuleset = memoize((rules = DEFAULT_RULES) => SimpleMarkdown.reactFor(
    SimpleMarkdown.ruleOutput(rules, 'react')
));

const parser = (source, { inline, rules }) => {
    const internalParser = getParserForRuleset(rules);
    let parsed;

    if (inline) {
        parsed = internalParser(source, { inline });
    } else {
        parsed = internalParser(`${source}\n\n`, { inline });

        // discard the top-most wrapper, since there's another being introduced
        // in the actual React component
        if (parsed.length === 1) {
            parsed = parsed[0].content;
        }
    }

    return parsed;
};

/**
 * A React component for rendering markdown strings. The baseline implementation should be sufficient for most
 * use cases, but the ability to extend the parser and emitter engines is provided via the prop `rules`. See the
 * [simple-markdown](https://github.com/Khan/simple-markdown) repository for examples of how to do this. The code
 * is also very concise, so it's easy to jog through the source to learn how things work or borrow a regex.
 *
 * @param  {Boolean}                options.inline  - allows for the activation of "inline" mode, meaning no
 *                                                    block-level markdown syntaxes like headings, fenced code blocks,
 *                                                    images, etc will be processed; it's best used when you want a
 *                                                    modicum of textual styling and perhaps a link but not the
 *                                                    ability to render very rich content
 *
 * @param  {Object}                 options.rules   - pass an overriden version of `DEFAULT_RULES` (it's exported) if
 *                                                    desired to override the parser and emitter engines; take a look at
 *                                                    the source of this component to see a practical example
 *
 * @param  {String}                 options.source  - the markdown content to be processed and rendered
 *
 * @param  {String|ReactComponent}  options.tag:    - override the default wrapper element if desired; pass either a
 *                                                    string tag name like "div" or a custom React component, but make
 *                                                    sure the custom component does not meddle with the `className`
 *                                                    or `children` props
 */
const Markdown = ({
    inline = false, rules, source, tag: Component = 'div', ...props
}) => (
    <Component {...props}>
        {getEmitterForRuleset(rules)(parser(source, {
            inline,
            rules,
        }))}
    </Component>
);

Markdown.propTypes = {
    source: PropTypes.string.isRequired,
};

export { Markdown };
